import { useEffect, useState, type FormEvent } from 'react';
import { motion } from 'motion/react';
import { ArrowRight, Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useTheme } from '@/src/contexts/ThemeContext';
import ThemeToggle from '@/src/components/ThemeToggle';
import { recoveryLinkError } from '@/src/lib/recovery';

function useColors() {
  const { isDark } = useTheme();
  return {
    isDark,
    bg: isDark ? '#0F0F0D' : '#FAF8F9',
    text: isDark ? 'rgba(255,255,255,0.9)' : '#1D1D18',
    textDim: isDark ? 'rgba(255,255,255,0.5)' : '#6E6E64',
    textFaint: isDark ? 'rgba(255,255,255,0.25)' : '#A6A69A',
    label: isDark ? '#A3A3A3' : '#6E6E64',
    inputBg: isDark ? '#171717' : '#FFFFFF',
    inputBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)',
    inputBorderFocus: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)',
    icon: isDark ? '#737373' : '#A3A3A3',
    accent: '#2E5A32',
    accentSoft: isDark ? '#8FD08F' : '#2E5A32',
    danger: isDark ? '#F87171' : '#DC2626',
  };
}

export default function ResetPassword({ onDone }: { onDone: () => void }) {
  const c = useColors();
  const [status, setStatus] = useState<'checking' | 'ready' | 'invalid' | 'done'>('checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // The reset link signs the user in temporarily. Wait for that session;
  // if none arrives, the link is expired or already used.
  useEffect(() => {
    if (recoveryLinkError) { setStatus('invalid'); return; }
    let settled = false;
    const markReady = () => { if (!settled) { settled = true; setStatus('ready'); } };

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) markReady();
    });
    void supabase.auth.getSession().then(({ data }) => { if (data.session) markReady(); });
    const t = setTimeout(() => { if (!settled) { settled = true; setStatus('invalid'); } }, 4000);

    return () => { sub.subscription.unsubscribe(); clearTimeout(t); };
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError('Use at least 8 characters.'); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setSaving(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    if (err) { setSaving(false); setError(err.message); return; }
    // End the temporary reset session AND every other session (other browsers,
    // the iOS app), so the new password is required everywhere.
    await supabase.auth.signOut({ scope: 'global' });
    setSaving(false);
    setStatus('done');
  }

  function finish() {
    window.history.replaceState(null, '', '/');
    onDone();
  }

  const inputStyle = { background: c.inputBg, border: `1px solid ${c.inputBorder}`, color: c.text };
  const focus = (e: React.FocusEvent<HTMLInputElement>) => { e.currentTarget.style.borderColor = c.inputBorderFocus; };
  const blur = (e: React.FocusEvent<HTMLInputElement>) => { e.currentTarget.style.borderColor = c.inputBorder; };

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center px-6" style={{ background: c.bg }}>
      <ThemeToggle fixed />
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-sm">
        {status === 'checking' && (
          <p className="text-sm text-center" style={{ color: c.textDim }}>Checking your reset link…</p>
        )}

        {status === 'invalid' && (
          <div className="text-center">
            <h1 className="text-xl font-semibold" style={{ color: c.text }}>Link expired</h1>
            <p className="text-sm mt-2" style={{ color: c.textDim }}>
              This reset link is invalid or has already been used. Request a new one from the sign-in screen.
            </p>
            <button onClick={finish} className="mt-6 text-xs font-bold uppercase tracking-widest" style={{ color: c.accentSoft }}>
              Back to sign in
            </button>
          </div>
        )}

        {status === 'done' && (
          <div className="text-center">
            <CheckCircle2 size={40} className="mx-auto mb-4" style={{ color: c.accentSoft }} />
            <h1 className="text-xl font-semibold" style={{ color: c.text }}>Password updated</h1>
            <p className="text-sm mt-2" style={{ color: c.textDim }}>
              For security, you've been signed out on all devices. Sign in with your new password, here or in the OpsAlo app.
            </p>
            <button onClick={finish} className="mt-6 text-xs font-bold uppercase tracking-widest" style={{ color: c.accentSoft }}>
              Sign in
            </button>
          </div>
        )}

        {status === 'ready' && (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="text-center mb-6">
              <h1 className="text-xl font-semibold" style={{ color: c.text }}>Set a new password</h1>
              <p className="text-sm mt-2" style={{ color: c.textDim }}>Choose a password with at least 8 characters.</p>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium" style={{ color: c.label }}>New password</label>
              <div className="relative">
                <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: c.icon }} />
                <input
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}  
                  autoFocus
                  className="w-full h-12 rounded-xl pl-11 pr-11 text-sm focus:outline-none transition-colors"
                  style={inputStyle} onFocus={focus} onBlur={blur}
                />
                <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-4 top-1/2 -translate-y-1/2" style={{ color: c.icon }} aria-label={show ? 'Hide password' : 'Show password'}>
                  {show ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium" style={{ color: c.label }}>Confirm password</label>
              <div className="relative">
                <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: c.icon }} />
                <input
                  type={show ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  className="w-full h-12 rounded-xl pl-11 pr-4 text-sm focus:outline-none transition-colors"
                  style={inputStyle} onFocus={focus} onBlur={blur}
                />
              </div>
            </div>

            {error && <p className="text-[11px] px-1" style={{ color: c.danger }}>{error}</p>}

            <motion.button
              type="submit"
              disabled={saving || !password || !confirm}
              whileTap={{ scale: 0.97 }}
              className="w-full h-12 mt-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:cursor-not-allowed transition-colors"
              style={{
                background: saving || !password || !confirm ? (c.isDark ? 'rgba(255,255,255,0.08)' : '#E7E5E0') : c.accent,
                color: saving || !password || !confirm ? c.textFaint : 'white',
              }}
            >
              {saving ? 'Saving…' : 'Update password'}
              {!saving && <ArrowRight size={16} />}
            </motion.button>
          </form>
        )}
      </motion.div>
    </div>
  );
}